import type { ScenarioContext } from "@/serverSizing/types";

export interface DesignInputIssue {
  code: string;
  section: "Well Data" | "Operating Parameters";
  field: string;
  message: string;
}

/** Readiness concerns source inputs only, never existing selected equipment.
 * Zero rates/pressures can be valid; absent values must not become defaults.
 */
export function designInputIssues(context: ScenarioContext): DesignInputIssue[] {
  const well = (context.wellProperties || {}) as Record<string, unknown>;
  const parameters = (context.parameters || {}) as Record<string, unknown>;
  const issues: DesignInputIssue[] = [];
  function requireNumber(
    source: Record<string, unknown>, field: string, label: string,
    section: DesignInputIssue["section"], minimum = 0, exclusive = false
  ) {
    const value = source[field];
    if (typeof value !== "number" || !Number.isFinite(value) ||
        (exclusive ? value <= minimum : value < minimum)) {
      issues.push({ code: "required_design_input", section, field,
        message: `${section}: enter ${label}.` });
    }
  }
  for (const [field, label] of [
    ["top_casing_length_feet", "top casing length"],
    ["top_of_perforations_measured_depth_feet", "perforation measured depth"],
    ["top_of_perforations_true_vertical_depth_feet", "perforation vertical depth"],
    ["well_test_datum_point_measured_depth_feet", "well test datum depth"],
    ["well_test_static_bottom_hole_pressure_psia", "static bottomhole pressure"],
    ["water_specific_gravity", "water specific gravity"],
    ["gas_specific_gravity", "gas specific gravity"]
  ]) requireNumber(well, field, label, "Well Data", 0, true);
  for (const [field, label] of [
    ["oil_gravity_api", "oil API gravity"],
    ["well_test_gas_rate_mscf_day", "well test gas rate"]
  ]) requireNumber(well, field, label, "Well Data");
  // An absent override uses the selected PVT correlation in the manual engine.
  if (well.bubble_point_pressure_psia != null)
    requireNumber(well, "bubble_point_pressure_psia", "bubble point pressure", "Well Data");
  for (const field of ["surface_temperature_fahrenheit", "bottomhole_temperature_fahrenheit"]) {
    requireNumber(well, field, field.replaceAll("_", " "), "Well Data", -459.67, true);
  }
  if (!well.top_casing_id || !well.r_top_casing) {
    issues.push({ code: "required_design_input", section: "Well Data", field: "top_casing_id", message: "Well Data: select top casing." });
  }
  if (well.well_test_liquid_input_type === "productivity_index_water_cut") {
    requireNumber(well, "well_test_productivity_index", "productivity index", "Well Data", 0, true);
  } else {
    // Fluid-level mode persists equivalent pressure, just as GOR/GLR and
    // rate/water-cut modes persist normalized gas/oil/water rates.
    requireNumber(well, "well_test_producing_bottom_hole_pressure_psia", "producing bottomhole pressure", "Well Data");
  }
  {
    requireNumber(well, "well_test_oil_rate_stb_day", "well test oil rate", "Well Data");
    requireNumber(well, "well_test_water_rate_stb_day", "well test water rate", "Well Data");
    if (well.well_test_oil_rate_stb_day === 0 && well.well_test_water_rate_stb_day === 0) {
      issues.push({ code: "invalid_design_input", section: "Well Data", field: "well_test_oil_rate_stb_day", message: "Well Data: well test total liquid rate must be positive." });
    }
  }
  requireNumber(well, "top_tubing_length_feet", "production tubing length", "Operating Parameters", 0, true);
  if (!well.top_tubing_id || !well.r_top_tubing) {
    issues.push({ code: "required_design_input", section: "Operating Parameters", field: "top_tubing_id", message: "Operating Parameters: select production tubing." });
  }
  for (const [field, label] of [
    ["target_liquid_rate_std_barrel_day", "target liquid rate"],
    ["pump_hz", "operating frequency"]
  ]) requireNumber(parameters, field, label, "Operating Parameters", 0, true);
  for (const [field, label] of [
    ["bottom_tubing_pressure_psia", "tubing pressure"],
    ["bottom_casing_pressure_psia", "casing pressure"]
  ]) requireNumber(parameters, field, label, "Operating Parameters");
  if (well.well_test_liquid_input_type !== "productivity_index_water_cut" &&
      typeof well.well_test_static_bottom_hole_pressure_psia === "number" &&
      typeof well.well_test_producing_bottom_hole_pressure_psia === "number" &&
      well.well_test_static_bottom_hole_pressure_psia <= well.well_test_producing_bottom_hole_pressure_psia) {
    issues.push({ code: "invalid_design_input", section: "Well Data", field: "well_test_static_bottom_hole_pressure_psia", message: "Well Data: static pressure must exceed producing pressure." });
  }
  for (const prefix of ["bottom_casing", "bottom_tubing"]) {
    if (well[`${prefix}_id`]) {
      requireNumber(well, `${prefix}_length_feet`, `${prefix.replaceAll("_", " ")} length`, prefix.includes("tubing") ? "Operating Parameters" : "Well Data", 0, true);
    }
  }
  return issues;
}
